import React from "react";
import { Dropdown, Button } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { confirmar } from "../../../../ui/confirmar";
import "./CardActionsMenu.scss";

/**
 * =============================================================================
 * MENU DE ACCIONES DE UNA TARJETA
 * =============================================================================
 * Las acciones secundarias de una tarjeta se recogen aqui en vez de vivir como
 * iconos sueltos junto a la accion principal. Dos motivos:
 *
 *  - Espacio: tres iconos de 24 px en una fila de 375 px son tres dianas
 *    pequenas pegadas entre si, y la de borrar esta a seis pixeles de la que se
 *    usa cada dia.
 *  - Seguridad: lo destructivo va SIEMPRE al final, detras de un filete y
 *    separado, y confirma nombrando lo que destruye.
 *
 * Cada accion es `{ key, icon, label, run, peligroso?, confirmacion? }`. Si
 * lleva `confirmacion`, se pregunta antes de ejecutar `run`.
 * =============================================================================
 */
export function CardActionsMenu({ items = [], ariaLabel }) {
  const { t } = useTranslation("common");
  if (!items.length) return null;

  // Lo peligroso, al final y separado por un filete.
  const normales = items.filter((i) => !i.peligroso);
  const peligrosas = items.filter((i) => i.peligroso);

  const aEntrada = (accion) => ({
    key: accion.key,
    icon: accion.icon,
    label: accion.label,
    danger: accion.peligroso,
    onClick: ({ domEvent }) => {
      domEvent?.stopPropagation?.();
      if (!accion.confirmacion) return accion.run?.();
      return confirmar({ ...accion.confirmacion, onOk: accion.run });
    },
  });

  const menuItems = [
    ...normales.map(aEntrada),
    ...(peligrosas.length && normales.length
      ? [{ type: "divider", key: "sep" }]
      : []),
    ...peligrosas.map(aEntrada),
  ];

  return (
    // El contenedor para el clic: sin esto, tocar el kebab abriria ademas el
    // registro, porque la fila entera es la diana.
    <div
      className="revi-card-kebab"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      role="presentation"
    >
      <Dropdown
        trigger={["click"]}
        placement="bottomRight"
        menu={{ items: menuItems, className: "revi-card-kebab__menu" }}
        // El menu se ancla dentro del cajon o del dialogo si esta en uno: si no,
        // se queda pegado al body y no sigue al scroll de su contenedor.
        getPopupContainer={(n) =>
          n.closest(".ant-drawer-body") ||
          n.closest(".ant-modal-body") ||
          document.body
        }
      >
        <Button
          type="text"
          icon={<MoreOutlined />}
          className="revi-card-kebab__btn"
          aria-label={ariaLabel || t("actions.moreOptions")}
        />
      </Dropdown>
    </div>
  );
}
